import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import AddProspectSheet from "@/components/AddProspectSheet";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Filter, List, Sparkles, Locate, Navigation, Route, Clock, ExternalLink,
  Loader2, ChevronRight, Target,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap, CircleMarker } from "react-leaflet";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl: markerIcon, shadowUrl: markerShadow });

function coloredIcon(color) {
  return L.divIcon({
    className: "",
    html: `<div style="width:26px;height:26px;border-radius:50%;background:${color};border:3px solid #09090B;box-shadow:0 2px 6px rgba(0,0,0,0.6)"></div>`,
    iconSize: [26, 26], iconAnchor: [13, 13],
  });
}

function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, map.getZoom()); }, [center, map]);
  return null;
}

const TARGET_OPTIONS = [
  { code: "", label: "Toutes les entreprises" },
  { code: "F", label: "BTP & Construction" },
  { code: "I", label: "Restauration & Hébergement" },
  { code: "G", label: "Commerce & Distribution" },
  { code: "M", label: "Services aux entreprises" },
  { code: "Q", label: "Santé & Action sociale" },
  { code: "J", label: "IT & Communication" },
  { code: "K", label: "Finance & Assurance" },
  { code: "L", label: "Immobilier" },
  { code: "H", label: "Transport & Logistique" },
];

const DURATION_OPTIONS = [
  { min: 60, label: "1h" }, { min: 120, label: "2h" }, { min: 180, label: "3h" },
  { min: 240, label: "4h" }, { min: 360, label: "Journée" },
];
const VISIT_DURATIONS = [15, 20, 30, 45];

function haversine(a, b) {
  const R = 6371, dLat = ((b[0] - a[0]) * Math.PI) / 180, dLng = ((b[1] - a[1]) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos((a[0] * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export default function Terrain() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "carte";
  const setTab = (t) => setSearchParams({ tab: t });

  const [pos, setPos] = useState([48.8566, 2.3522]);
  const [geoStatus, setGeoStatus] = useState("idle");
  const [prospects, setProspects] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  const [radius, setRadius] = useState(5);
  const [minScore, setMinScore] = useState(0);
  const [viewList, setViewList] = useState(false);
  const [nafSection, setNafSection] = useState("");
  const [nearby, setNearby] = useState([]);
  const [searchingNearby, setSearchingNearby] = useState(false);
  const [addingSiret, setAddingSiret] = useState(null);

  const now = new Date();
  const nextHour = new Date(now);
  nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);
  const [duration, setDuration] = useState(180);
  const [visitDuration, setVisitDuration] = useState(20);
  const [startTime, setStartTime] = useState(nextHour.toTimeString().slice(0, 5));
  const [postal, setPostal] = useState("");
  const [city, setCity] = useState("");
  const [useGeoloc, setUseGeoloc] = useState(false);
  const [tourRadius, setTourRadius] = useState(10);
  const [coords, setCoords] = useState(null);
  const [geoError, setGeoError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/prospects").then(({ data }) => setProspects(data || [])).catch(() => {});
    navigator.geolocation?.getCurrentPosition(
      (p) => { setPos([p.coords.latitude, p.coords.longitude]); setGeoStatus("granted"); },
      (err) => setGeoStatus(err.code === 1 ? "denied" : "idle"),
      { timeout: 7000 }
    );
  }, []);

  useEffect(() => {
    if (!useGeoloc) return;
    if (!navigator.geolocation) { setGeoError("Géolocalisation indisponible"); return; }
    setGeoError("Localisation en cours…");
    navigator.geolocation.getCurrentPosition(
      (p) => { setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }); setGeoError(""); },
      () => setGeoError("Autorisez la géolocalisation"),
      { timeout: 8000 }
    );
  }, [useGeoloc]);

  const requestGeoloc = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation indisponible"); return; }
    setGeoStatus("requesting");
    navigator.geolocation.getCurrentPosition(
      (p) => { setPos([p.coords.latitude, p.coords.longitude]); setGeoStatus("granted"); toast.success("Position captée"); },
      (err) => { setGeoStatus("denied"); toast.error(err.code === 1 ? "Autorisez la géolocalisation dans les réglages" : "Position introuvable"); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  const filtered = useMemo(() =>
    prospects
      .filter((p) => p.gps_lat && p.gps_lng)
      .map((p) => ({ ...p, distance: haversine(pos, [p.gps_lat, p.gps_lng]) }))
      .filter((p) => p.distance <= radius && p.score >= minScore)
      .sort((a, b) => a.distance - b.distance),
    [prospects, pos, radius, minScore]
  );

  const colorFor = (s) => (s >= 80 ? "#10B981" : s >= 55 ? "#F59E0B" : "#38BDF8");

  const findNearby = async () => {
    setSearchingNearby(true); setNearby([]);
    try {
      const param = nafSection ? `&section=${nafSection}` : "";
      const { data } = await api.get(`/siret/nearby?lat=${pos[0]}&lng=${pos[1]}&radius=${radius}${param}&limit=20`);
      if (!data.length) { toast.info(`Aucune cible dans ${radius} km`); }
      else toast.success(`${data.length} cible${data.length > 1 ? "s" : ""} autour de vous`);
      const existing = new Set(prospects.map((p) => p.siret).filter(Boolean));
      setNearby(data.filter((c) => !c.siret || !existing.has(c.siret)));
    } catch { toast.error("Service indisponible"); }
    finally { setSearchingNearby(false); }
  };

  const addNearbyAsLead = async (c) => {
    setAddingSiret(c.siret);
    try {
      await api.post("/prospects", {
        name: c.company_name || "", company: c.company_name || "", siret: c.siret || "",
        address: c.address || "", postal_code: c.postal_code || "", city: c.city || "",
        gps_lat: c.gps_lat, gps_lng: c.gps_lng, sector: c.naf_label || "",
        score: 60, status: "À contacter", source: "Road Maps",
      });
      toast.success(`${c.company_name} ajouté`);
      setNearby((xs) => xs.filter((x) => x.siret !== c.siret));
      api.get("/prospects").then(({ data }) => setProspects(data || [])).catch(() => {});
    } catch { toast.error("Impossible d'ajouter ce lead"); }
    finally { setAddingSiret(null); }
  };

  const planTour = async () => {
    setLoading(true); setResult(null);
    try {
      const [hh, mm] = startTime.split(":").map(Number);
      const start = new Date(); start.setHours(hh, mm, 0, 0);
      const zone = {};
      if (postal.trim()) zone.postal_prefix = postal.trim();
      if (city.trim()) zone.city = city.trim();
      if (useGeoloc && coords) { zone.lat = coords.lat; zone.lng = coords.lng; zone.radius_km = tourRadius; }
      const body = { duration_min: duration, visit_duration_min: visitDuration, start_time: start.toISOString(), max_stops: 8, zone };
      if (useGeoloc && coords) body.start_point = { lat: coords.lat, lng: coords.lng };
      const { data } = await api.post("/tour/plan", body);
      setResult(data);
      toast.success(`${data.stops.length} visites planifiées`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Aucune tournée planifiable"); }
    finally { setLoading(false); }
  };

  // Segmented control helper
  const segBtn = (active) =>
    `py-3 text-sm font-medium transition-colors ${
      active ? "bg-emerald-500 text-white" : "text-zinc-400 hover:bg-[var(--surface-alt)]"
    }`;

  return (
    <AppShell>
      {/* HEADER */}
      <div className="px-5 pt-5 pb-3 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="hp-label">Road Maps</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold text-[var(--text)] mt-0.5 tracking-tight leading-[0.98]">
            {tab === "carte" ? "Maps" : "Ma tournée"}
          </h1>
        </div>
        <div className="flex gap-0 border border-[var(--border)] rounded-lg overflow-hidden shrink-0" data-testid="terrain-tabs">
          <button
            onClick={() => setTab("carte")}
            data-testid="tab-carte"
            className={`px-4 py-2 text-sm font-medium transition-colors ${tab === "carte" ? "bg-emerald-500 text-white" : "text-zinc-400 hover:bg-[var(--surface-alt)]"}`}
          >
            Maps
          </button>
          <button
            onClick={() => { setTab("tournee"); setResult(null); }}
            data-testid="tab-tournee"
            className={`px-4 py-2 text-sm font-medium border-l border-[var(--border)] transition-colors ${tab === "tournee" ? "bg-emerald-500 text-white" : "text-zinc-400 hover:bg-[var(--surface-alt)]"}`}
          >
            Tournée
          </button>
        </div>
      </div>

      {tab === "carte" && (
        <>
          {/* Full-bleed map */}
          <div className="relative border-y border-[var(--border)]" style={{ height: "calc(100vh - 130px)" }}>
            <MapContainer center={pos} zoom={13} style={{ height: "100%", width: "100%", background: "#09090B" }} scrollWheelZoom={false} zoomControl={false}>
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://carto.com/">CARTO</a>'
              />
              <MapRecenter center={pos} />
              {filtered.map((p) => (
                <Marker key={p.prospect_id} position={[p.gps_lat, p.gps_lng]} icon={coloredIcon(colorFor(p.score))}>
                  <Popup>
                    <div className="font-semibold text-sm">{p.name || p.company}</div>
                    <div className="text-xs text-gray-600">{p.company} · {p.score}%</div>
                    <button onClick={() => navigate(`/prospect/${p.prospect_id}`)} className="mt-2 text-xs font-semibold underline">Voir le lead →</button>
                  </Popup>
                </Marker>
              ))}
              {nearby.map((c, i) => c.gps_lat && c.gps_lng && (
                <CircleMarker key={i} center={[c.gps_lat, c.gps_lng]} radius={10} pathOptions={{ color: "#00DC82", fillColor: "#00DC82", fillOpacity: 0.8 }}>
                  <Popup>
                    <div className="font-semibold text-sm">{c.company_name}</div>
                    <div className="text-xs text-gray-600">{c.naf_label}</div>
                    <button onClick={() => addNearbyAsLead(c)} disabled={addingSiret === c.siret} className="mt-2 text-xs font-semibold underline">+ Ajouter comme lead</button>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>

            {/* Floating controls */}
            <div className="absolute top-3 right-3 z-[1000] flex flex-col gap-2">
              <button onClick={requestGeoloc} data-testid="btn-locate"
                className={`w-11 h-11 rounded-lg flex items-center justify-center transition-all active:scale-95 border ${
                  geoStatus === "granted"
                    ? "bg-emerald-500 text-white border-emerald-500"
                    : "bg-[var(--surface)] text-zinc-300 border-[var(--border)] hover:border-zinc-700"
                }`}
                style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.45)" }}
              >
                <Locate className="w-5 h-5" strokeWidth={1.8} />
              </button>
              <Sheet>
                <SheetTrigger asChild>
                  <button data-testid="open-filters"
                    className="w-11 h-11 rounded-lg bg-[var(--surface)] border border-[var(--border)] hover:border-zinc-700 flex items-center justify-center text-zinc-300 active:scale-95 transition-all"
                    style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.45)" }}
                  >
                    <Filter className="w-5 h-5" strokeWidth={1.8} />
                  </button>
                </SheetTrigger>
                <SheetContent side="bottom" className="rounded-t-2xl max-h-[70vh] bg-[var(--surface)] border-t border-[var(--border)] text-[var(--text)]">
                  <SheetHeader><SheetTitle className="text-[var(--text)]">Filtres</SheetTitle></SheetHeader>
                  <div className="space-y-5 pt-4 pb-6">
                    <div>
                      <div className="hp-label mb-2">Rayon : <strong className="text-[var(--text)]">{radius} km</strong></div>
                      <div className="flex gap-2">
                        {[2, 5, 10, 20, 50].map((r) => (
                          <button key={r} onClick={() => setRadius(r)}
                            className={`flex-1 py-2.5 text-xs font-medium rounded-lg border transition-colors ${
                              radius === r ? "bg-emerald-500 text-white border-emerald-500" : "bg-[var(--surface-alt)] border-[var(--border)] text-zinc-300 hover:border-zinc-700"
                            }`}
                          >{r} km</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="hp-label mb-2">Score min : <strong className="text-[var(--text)]">{minScore}%</strong></div>
                      <div className="flex gap-2">
                        {[0, 40, 60, 80].map((s) => (
                          <button key={s} onClick={() => setMinScore(s)}
                            className={`flex-1 py-2.5 text-xs font-medium rounded-lg border transition-colors ${
                              minScore === s ? "bg-emerald-500 text-white border-emerald-500" : "bg-[var(--surface-alt)] border-[var(--border)] text-zinc-300 hover:border-zinc-700"
                            }`}
                          >{s === 0 ? "Tous" : `≥${s}%`}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="hp-label mb-2">Type de cibles</div>
                      <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                        {TARGET_OPTIONS.map((o) => (
                          <button key={o.code} onClick={() => setNafSection(o.code)}
                            className={`flex items-center gap-2 px-3 py-2.5 text-xs font-medium rounded-lg border text-left transition-colors ${
                              nafSection === o.code ? "bg-emerald-500 text-white border-emerald-500" : "bg-[var(--surface-alt)] border-[var(--border)] text-zinc-300 hover:border-zinc-700"
                            }`}
                          >
                            <span className="truncate">{o.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Bottom floating actions */}
            <div className="absolute bottom-4 left-3 z-[1000] flex gap-2">
              <button onClick={findNearby} disabled={searchingNearby} data-testid="btn-nearby"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold active:scale-95 transition-colors disabled:opacity-60 border border-emerald-500"
                style={{ boxShadow: "0 8px 24px rgba(0,220,130,0.35)" }}
              >
                <Target className="w-4 h-4" strokeWidth={1.8} />
                {searchingNearby ? "Recherche…" : "Trouver des cibles"}
              </button>
              {filtered.length > 0 && (
                <button onClick={() => setViewList(v => !v)} data-testid="toggle-view"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[var(--surface)] border border-[var(--border)] text-zinc-200 text-xs font-semibold active:scale-95 transition-colors"
                  style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.45)" }}
                >
                  <List className="w-4 h-4" strokeWidth={1.8} />
                  <span className="tabular-nums">{filtered.length}</span>
                </button>
              )}
            </div>
          </div>

          {/* Leads bottom sheet */}
          {viewList && filtered.length > 0 && (
            <div className="fixed bottom-16 left-0 right-0 z-40 bg-[var(--surface)] rounded-t-2xl border-t border-[var(--border)] max-h-64 overflow-y-auto" style={{ boxShadow: "0 -20px 45px rgba(0,0,0,0.55)" }}>
              <div className="px-5 pt-3 pb-1 flex items-center justify-between sticky top-0 bg-[var(--surface)] border-b border-[var(--border-soft)]">
                <span className="hp-label">{filtered.length} leads dans le rayon</span>
                <button onClick={() => setViewList(false)} className="text-zinc-500 hover:text-white text-lg">×</button>
              </div>
              {filtered.map((p) => (
                <button key={p.prospect_id} onClick={() => navigate(`/prospect/${p.prospect_id}`)}
                  className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-[var(--surface-alt)]/60 transition-colors border-b border-[var(--border-soft)]"
                >
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 grid place-items-center text-emerald-300 font-semibold text-sm shrink-0">
                    {(p.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[var(--text)] truncate">{p.name || p.company}</div>
                    <div className="text-xs text-zinc-500 truncate">{p.distance?.toFixed(1)} km · {p.city || p.company}</div>
                  </div>
                  <div className={`text-xs font-semibold tabular-nums ${p.score >= 80 ? "text-emerald-300" : p.score >= 60 ? "text-amber-300" : "text-zinc-400"}`}>{p.score}%</div>
                </button>
              ))}
            </div>
          )}

          {/* Nearby results sheet */}
          {nearby.length > 0 && !viewList && (
            <div className="fixed bottom-16 left-0 right-0 z-40 bg-[var(--surface)] rounded-t-2xl border-t border-[var(--border)] max-h-52 overflow-y-auto" style={{ boxShadow: "0 -20px 45px rgba(0,0,0,0.55)" }}>
              <div className="px-5 pt-3 pb-1 flex items-center justify-between sticky top-0 bg-[var(--surface)] border-b border-[var(--border-soft)]">
                <span className="hp-label" style={{ color: "#86efac" }}>{nearby.length} cibles trouvées</span>
                <button onClick={() => setNearby([])} className="text-zinc-500 hover:text-white text-lg">×</button>
              </div>
              {nearby.map((c, i) => (
                <div key={i} className="flex items-center gap-3 px-5 py-3 border-b border-[var(--border-soft)]">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[var(--text)] truncate">{c.company_name}</div>
                    <div className="text-xs text-zinc-500 truncate">{c.naf_label} · {c.city}</div>
                  </div>
                  <button onClick={() => addNearbyAsLead(c)} disabled={addingSiret === c.siret} data-testid={`add-nearby-${i}`}
                    className="shrink-0 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 rounded-md active:scale-95 disabled:opacity-50 transition-colors">
                    {addingSiret === c.siret ? "…" : "+ Lead"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "tournee" && !result && (
        <>
          {/* Duration */}
          <div className="px-5 mt-4">
            <div className="hp-label mb-2">Temps disponible</div>
            <div className="grid grid-cols-5 gap-0 border border-[var(--border)] rounded-lg overflow-hidden" data-testid="duration-picker">
              {DURATION_OPTIONS.map((o, i) => (
                <button key={o.min} onClick={() => setDuration(o.min)} data-testid={`duration-${o.min}`}
                  className={`${segBtn(duration === o.min)} ${i > 0 ? "border-l border-[var(--border)]" : ""}`}
                >{o.label}</button>
              ))}
            </div>
          </div>

          {/* Start time */}
          <div className="px-5 mt-5">
            <div className="hp-label mb-1">Heure de départ</div>
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
              className="h-12 rounded-none border-0 border-b border-[var(--border)] focus-visible:border-emerald-500 focus-visible:ring-0 px-0 text-base w-32 bg-transparent text-[var(--text)]"
              data-testid="tour-start-time"
            />
          </div>

          {/* Visit duration */}
          <div className="px-5 mt-5">
            <div className="hp-label mb-2">Durée par visite</div>
            <div className="grid grid-cols-4 gap-0 border border-[var(--border)] rounded-lg overflow-hidden" data-testid="visit-dur-picker">
              {VISIT_DURATIONS.map((v, i) => (
                <button key={v} onClick={() => setVisitDuration(v)} data-testid={`visit-dur-${v}`}
                  className={`${segBtn(visitDuration === v)} ${i > 0 ? "border-l border-[var(--border)]" : ""}`}
                >{v} min</button>
              ))}
            </div>
          </div>

          {/* Zone */}
          <div className="px-5 mt-5">
            <div className="hp-label mb-2">Zone</div>
            <button onClick={() => setUseGeoloc(!useGeoloc)} data-testid="toggle-geoloc"
              className={`w-full flex items-center gap-3 p-3.5 rounded-lg border transition-colors ${
                useGeoloc
                  ? "bg-emerald-500/10 text-[var(--text)] border-emerald-500/40"
                  : "bg-[var(--surface)] border-[var(--border)] hover:border-zinc-700"
              }`}
            >
              <Navigation className={`w-4 h-4 shrink-0 ${useGeoloc ? "text-emerald-300" : "text-zinc-400"}`} strokeWidth={1.8} />
              <div className="flex-1 text-left">
                <div className="font-medium text-sm">Autour de moi</div>
                <div className="text-xs mt-0.5 text-zinc-500">
                  {useGeoloc ? (coords ? `Localisé · rayon ${tourRadius} km` : geoError || "Localisation…") : "Activer la géolocalisation"}
                </div>
              </div>
              <div className={`w-10 h-5 rounded-full relative transition-colors ${useGeoloc ? "bg-emerald-500" : "bg-[var(--surface-alt)] border border-[var(--border)]"}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${useGeoloc ? "translate-x-5" : "translate-x-0.5"}`} />
              </div>
            </button>
            {useGeoloc && (
              <div className="mt-3 flex gap-0 border border-[var(--border)] rounded-lg overflow-hidden">
                {[5, 10, 20, 50].map((r, i) => (
                  <button key={r} onClick={() => setTourRadius(r)} data-testid={`radius-${r}`}
                    className={`flex-1 py-2 text-xs font-medium transition-colors ${i > 0 ? "border-l border-[var(--border)]" : ""} ${tourRadius === r ? "bg-emerald-500 text-white" : "text-zinc-400 hover:bg-[var(--surface-alt)]"}`}
                  >{r} km</button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <div className="hp-label mb-1">Code postal</div>
                <Input placeholder="75 ou 75001" value={postal} onChange={(e) => setPostal(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  data-testid="tour-postal" className="h-12 rounded-none border-0 border-b border-[var(--border)] focus-visible:border-emerald-500 focus-visible:ring-0 px-0 text-base bg-transparent text-[var(--text)]"
                />
              </div>
              <div>
                <div className="hp-label mb-1">Ville</div>
                <Input placeholder="Paris, Lyon…" value={city} onChange={(e) => setCity(e.target.value)}
                  data-testid="tour-city" className="h-12 rounded-none border-0 border-b border-[var(--border)] focus-visible:border-emerald-500 focus-visible:ring-0 px-0 text-base bg-transparent text-[var(--text)]"
                />
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="px-5 mt-8">
            <button onClick={planTour} disabled={loading} data-testid="plan-tour"
              className="hp-btn hp-btn-primary w-full h-14 text-base"
            >
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" />Optimisation…</> : <><Sparkles className="w-5 h-5" />Planifier ma tournée<ChevronRight className="w-5 h-5" /></>}
            </button>
            <p className="mt-4 text-xs text-zinc-500 text-center">
              Prosperr optimise l'ordre des visites et génère l'itinéraire Google Maps.
            </p>
          </div>
        </>
      )}

      {tab === "tournee" && result && (
        <TourResult result={result} onReset={() => setResult(null)} navigate={navigate} />
      )}

      <div className="h-8" />
      <AddProspectSheet open={addOpen} onOpenChange={setAddOpen}
        onCreated={() => api.get("/prospects").then(({ data }) => setProspects(data || [])).catch(() => {})} />
    </AppShell>
  );
}

function TourResult({ result, onReset, navigate }) {
  const s = result.summary;
  const hh = Math.floor(s.total_duration_min / 60), mm = s.total_duration_min % 60;
  const totalLabel = hh > 0 ? `${hh}h${mm ? String(mm).padStart(2, "0") : ""}` : `${mm} min`;
  return (
    <div data-testid="tour-result">
      <div className="px-5">
        <div className="p-5 rounded-xl border border-emerald-500/40" style={{ background: "linear-gradient(135deg, rgba(0,220,130,0.22), rgba(0,220,130,0.05))" }}>
          <div className="hp-label flex items-center gap-1.5" style={{ color: "#86efac" }}>
            <Route className="w-3 h-3" /> Tournée planifiée
          </div>
          <div className="font-display text-5xl font-semibold mt-3 leading-none tracking-tight text-white tabular-nums">{s.stops_count}</div>
          <div className="text-sm text-zinc-300 mt-1">visite{s.stops_count > 1 ? "s" : ""} optimisée{s.stops_count > 1 ? "s" : ""}</div>
          <div className="mt-5 pt-5 border-t border-[var(--border)] grid grid-cols-3 gap-3 text-center">
            <div><div className="font-display text-xl font-semibold text-white tabular-nums">{totalLabel}</div><div className="text-[10px] text-zinc-500 uppercase mt-1 tracking-wider">Total</div></div>
            <div><div className="font-display text-xl font-semibold text-white tabular-nums">{s.total_km}</div><div className="text-[10px] text-zinc-500 uppercase mt-1 tracking-wider">km</div></div>
            <div><div className="font-display text-xl font-semibold text-white tabular-nums">{s.total_travel_min}</div><div className="text-[10px] text-zinc-500 uppercase mt-1 tracking-wider">min route</div></div>
          </div>
          <a href={result.maps_url} target="_blank" rel="noopener noreferrer" data-testid="open-maps"
            className="mt-5 hp-btn hp-btn-primary w-full h-12"
          >
            <ExternalLink className="w-4 h-4" /> Ouvrir dans Google Maps
          </a>
        </div>
      </div>
      <div className="px-5 mt-5">
        <div className="hp-label mb-3">Itinéraire</div>
        <div className="relative">
          <div className="absolute left-[19px] top-2 bottom-2 w-px bg-[var(--border)]" />
          <div className="space-y-3">
            {result.stops.map((stop) => (
              <div key={stop.prospect_id} className="relative flex gap-4">
                <div className="relative z-10 w-10 h-10 rounded-lg bg-emerald-500 text-white grid place-items-center font-semibold text-sm shrink-0 tabular-nums border border-emerald-500">{stop.order}</div>
                <button onClick={() => navigate(`/prospect/${stop.prospect_id}`)} data-testid={`stop-${stop.order}`}
                  className="flex-1 hp-card p-3.5 text-left hover:border-zinc-700 active:scale-[0.99] transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-[var(--text)] truncate">{stop.name || stop.company}</div>
                      {stop.address && <div className="text-xs text-zinc-500 truncate mt-0.5">{stop.address}</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display text-base font-semibold text-[var(--text)] tabular-nums">{stop.arrival_label}</div>
                      <div className="text-[10px] text-zinc-500 uppercase mt-0.5 tracking-wider">Arrivée</div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-400">
                    <span className="bg-[var(--surface-alt)] border border-[var(--border-soft)] px-2 py-0.5 rounded-md flex items-center gap-1"><Clock className="w-3 h-3" />{stop.slot_recommendation}</span>
                    {stop.distance_km_from_prev > 0 && <span>{stop.distance_km_from_prev} km · {stop.travel_min} min</span>}
                    {stop.score > 0 && <span className="ml-auto font-semibold text-[var(--text)] tabular-nums">{stop.score}%</span>}
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="px-5 mt-6">
        <button onClick={onReset} data-testid="reset-tour" className="hp-btn hp-btn-secondary w-full h-12">
          Nouvelle tournée
        </button>
      </div>
    </div>
  );
}
